import { api } from "../../lib/api";

/** Full Frame CRM — thin axios wrapper.
 *  All requests scope to the company via `?company_id=` when provided
 *  (required for super_admin who is not bound to a single company). For
 *  regular owners, the backend infers `company_id` from the JWT.
 */
const q = (companyId) => (companyId ? `?company_id=${encodeURIComponent(companyId)}` : "");

export async function getSummary(companyId, opts = {}) {
  // opts: { month?: "YYYY-MM", year?: "YYYY" }
  // When `year` is set, backend aggregates the full calendar year and
  // ignores `month` (you can't filter both at once).
  const parts = [];
  if (companyId) parts.push(`company_id=${encodeURIComponent(companyId)}`);
  if (opts.year) parts.push(`year=${encodeURIComponent(opts.year)}`);
  else if (opts.month) parts.push(`month=${encodeURIComponent(opts.month)}`);
  const query = parts.length ? `?${parts.join("&")}` : "";
  const { data } = await api.get(`/fullframe/summary${query}`);
  return data;
}

export async function listProductions(companyId) {
  const { data } = await api.get(`/fullframe/productions${q(companyId)}`);
  return data.productions || [];
}

export async function createProduction(payload, companyId) {
  const { data } = await api.post(`/fullframe/productions${q(companyId)}`, payload);
  return data;
}

export async function getProduction(id, companyId) {
  const { data } = await api.get(`/fullframe/productions/${id}${q(companyId)}`);
  return data;
}

export async function updateProduction(id, payload, companyId) {
  const { data } = await api.patch(`/fullframe/productions/${id}${q(companyId)}`, payload);
  return data;
}

export async function deleteProduction(id, companyId) {
  const { data } = await api.delete(`/fullframe/productions/${id}${q(companyId)}`);
  return data;
}

// Owner spec 2026-07-02 — replace the entire schedule array in one call.
export async function updateProductionSchedule(prodId, schedule, companyId) {
  const { data } = await api.put(`/fullframe/productions/${prodId}/schedule${q(companyId)}`, { schedule });
  return data;
}

// Owner spec 2026-07-06 — replace the entire crew_roster array in one call.
export async function updateProductionCrewRoster(prodId, crew_roster, companyId) {
  const { data } = await api.put(`/fullframe/productions/${prodId}/crew-roster${q(companyId)}`, { crew_roster });
  return data;
}

// Owner spec 2026-07-07c — duplicate a production (deep clone new ids).
export async function duplicateProduction(prodId, companyId) {
  const { data } = await api.post(`/fullframe/productions/${prodId}/duplicate${q(companyId)}`);
  return data;
}

export async function addExpenseRow(prodId, payload, companyId) {
  const { data } = await api.post(`/fullframe/productions/${prodId}/expenses${q(companyId)}`, payload);
  return data;
}

export async function updateExpenseRow(prodId, rowId, payload, companyId) {
  const { data } = await api.patch(`/fullframe/productions/${prodId}/expenses/${rowId}${q(companyId)}`, payload);
  return data;
}

export async function deleteExpenseRow(prodId, rowId, companyId) {
  const { data } = await api.delete(`/fullframe/productions/${prodId}/expenses/${rowId}${q(companyId)}`);
  return data;
}

// ----- Additional expenses (uploaded receipt photos) -----

export async function uploadAdditionalExpense(prodId, file, companyId, runOcr = true) {
  const fd = new FormData();
  if (file) fd.append("file", file);
  fd.append("run_ocr", file && runOcr ? "1" : "0");
  // IMPORTANT: do NOT set Content-Type manually — axios needs to set the
  // multipart boundary itself. Forcing "multipart/form-data" without a
  // boundary breaks the request on some browsers (desktop Chrome reported
  // the upload silently failing).
  const { data } = await api.post(
    `/fullframe/productions/${prodId}/additional${q(companyId)}`,
    fd,
    { timeout: 120000 },
  );
  return data;
}

export async function updateAdditionalExpense(prodId, rowId, payload, companyId) {
  const { data } = await api.patch(`/fullframe/productions/${prodId}/additional/${rowId}${q(companyId)}`, payload);
  return data;
}

export async function deleteAdditionalExpense(prodId, rowId, companyId) {
  const { data } = await api.delete(`/fullframe/productions/${prodId}/additional/${rowId}${q(companyId)}`);
  return data;
}

export async function getFullFrameConfig() {
  const { data } = await api.get("/fullframe/config");
  return data;
}

// ----- Suppliers (autocomplete) -----

export async function searchSuppliers(query, companyId) {
  const params = new URLSearchParams();
  if (query) params.set("q", query);
  if (companyId) params.set("company_id", companyId);
  const { data } = await api.get(`/fullframe/suppliers?${params.toString()}`);
  return data.suppliers || [];
}

export async function createSupplier({ name, phone, address, email, category, categories }, companyId) {
  const params = new URLSearchParams();
  if (companyId) params.set("company_id", companyId);
  const payload = {
    name,
    phone: phone || "",
    address: address || "",
    email: email || "",
    category: category || "",
  };
  if (Array.isArray(categories)) payload.categories = categories;
  const { data } = await api.post(
    `/fullframe/suppliers?${params.toString()}`,
    payload
  );
  return data;
}

export async function deleteSupplier(name, companyId) {
  const params = new URLSearchParams();
  params.set("name", name);
  if (companyId) params.set("company_id", companyId);
  const { data } = await api.delete(`/fullframe/suppliers?${params.toString()}`);
  return data;
}

export async function getSupplierCategories(companyId) {
  const params = new URLSearchParams();
  if (companyId) params.set("company_id", companyId);
  const { data } = await api.get(`/fullframe/supplier-categories?${params.toString()}`);
  return data.slots || ["", "", "", "", "", ""];
}

export async function saveSupplierCategories(slots, companyId) {
  const params = new URLSearchParams();
  if (companyId) params.set("company_id", companyId);
  const { data } = await api.put(`/fullframe/supplier-categories?${params.toString()}`, { slots });
  return data.slots || slots;
}

// ----- Company-wide expenses (one_time / recurring) -----

export async function listCompanyExpenses(companyId) {
  const { data } = await api.get(`/fullframe/company-expenses${q(companyId)}`);
  return data.expenses || [];
}

export async function uploadCompanyExpense({ file, kind, startMonth, endMonth, companyId }) {
  const fd = new FormData();
  if (file) fd.append("file", file);
  fd.append("kind", kind || "one_time");
  if (startMonth) fd.append("start_month", startMonth);
  if (endMonth) fd.append("end_month", endMonth);
  fd.append("run_ocr", "1");
  const { data } = await api.post(
    `/fullframe/company-expenses${q(companyId)}`,
    fd,
    { timeout: 120000 },
  );
  return data;
}

export async function updateCompanyExpense(rowId, payload, companyId) {
  const { data } = await api.patch(`/fullframe/company-expenses/${rowId}${q(companyId)}`, payload);
  return data;
}

export async function deleteCompanyExpense(rowId, companyId) {
  const { data } = await api.delete(`/fullframe/company-expenses/${rowId}${q(companyId)}`);
  return data;
}

export async function getSummaryForMonth(month, companyId) {
  const params = new URLSearchParams();
  if (month) params.set("month", month);
  if (companyId) params.set("company_id", companyId);
  const url = `/fullframe/summary${params.toString() ? "?" + params.toString() : ""}`;
  const { data } = await api.get(url);
  return data;
}

export async function getSummaryForYear(year, companyId) {
  const params = new URLSearchParams();
  if (year) params.set("year", String(year));
  if (companyId) params.set("company_id", companyId);
  const url = `/fullframe/summary${params.toString() ? "?" + params.toString() : ""}`;
  const { data } = await api.get(url);
  return data;
}

// ----- Payments (split tracker) -----

export async function addPayment(prodId, payload, companyId) {
  const { data } = await api.post(`/fullframe/productions/${prodId}/payments${q(companyId)}`, payload);
  return data;
}

export async function updatePayment(prodId, payId, payload, companyId) {
  const { data } = await api.patch(`/fullframe/productions/${prodId}/payments/${payId}${q(companyId)}`, payload);
  return data;
}

export async function deletePayment(prodId, payId, companyId) {
  const { data } = await api.delete(`/fullframe/productions/${prodId}/payments/${payId}${q(companyId)}`);
  return data;
}

// ----- Crew-row payment installments (per production_team member) -----

export async function addCrewPayment(prodId, rowId, { amount, note, provider, providerPhone, providerWhatsapp, invoice }, companyId) {
  const fd = new FormData();
  fd.append("amount", String(Number(amount) || 0));
  if (note) fd.append("note", note);
  if (provider) fd.append("provider", provider);
  if (providerPhone) fd.append("provider_phone", providerPhone);
  if (providerWhatsapp) fd.append("provider_whatsapp", providerWhatsapp);
  if (invoice) fd.append("invoice", invoice);
  const { data } = await api.post(
    `/fullframe/productions/${prodId}/expenses/${rowId}/payments${q(companyId)}`,
    fd,
    { timeout: 120000 },
  );
  return data;
}

export async function deleteCrewPayment(prodId, rowId, payId, companyId) {
  const { data } = await api.delete(
    `/fullframe/productions/${prodId}/expenses/${rowId}/payments/${payId}${q(companyId)}`,
  );
  return data;
}

export async function replaceCrewPaymentInvoice(prodId, rowId, payId, file, companyId, { clear = false } = {}) {
  const fd = new FormData();
  if (file) fd.append("invoice", file);
  if (clear) fd.append("clear", "true");
  const { data } = await api.patch(
    `/fullframe/productions/${prodId}/expenses/${rowId}/payments/${payId}/invoice${q(companyId)}`,
    fd,
    { timeout: 120000 },
  );
  return data;
}

// ----- Partner earnings (Daniel & Nir) -----

export async function getPartnerEarnings({ month, year } = {}, companyId) {
  const params = new URLSearchParams();
  if (month) params.set("month", month);
  if (year) params.set("year", String(year));
  if (companyId) params.set("company_id", companyId);
  const url = `/fullframe/partner-earnings${params.toString() ? "?" + params.toString() : ""}`;
  const { data } = await api.get(url);
  return data;
}

export async function getPartnerDetails(key, companyId) {
  const { data } = await api.get(`/fullframe/partner-earnings/${key}/details${q(companyId)}`);
  return data;
}

// ----- Additional contacts (max 10 per production) -----

export async function addContact(prodId, payload, companyId) {
  const { data } = await api.post(`/fullframe/productions/${prodId}/contacts${q(companyId)}`, payload);
  return data;
}

export async function updateContact(prodId, contactId, payload, companyId) {
  const { data } = await api.patch(`/fullframe/productions/${prodId}/contacts/${contactId}${q(companyId)}`, payload);
  return data;
}

export async function deleteContact(prodId, contactId, companyId) {
  const { data } = await api.delete(`/fullframe/productions/${prodId}/contacts/${contactId}${q(companyId)}`);
  return data;
}

// ----- Price proposal (quote) — image / PDF / external link -----

export async function uploadQuoteFile(prodId, file, companyId) {
  const fd = new FormData();
  fd.append("file", file);
  const { data } = await api.post(
    `/fullframe/productions/${prodId}/quote${q(companyId)}`,
    fd,
    { timeout: 120000 },
  );
  return data;
}

export async function uploadQuoteLink(prodId, link, companyId) {
  const fd = new FormData();
  fd.append("link", link);
  const { data } = await api.post(`/fullframe/productions/${prodId}/quote${q(companyId)}`, fd);
  return data;
}

export async function deleteQuote(prodId, companyId) {
  const { data } = await api.delete(`/fullframe/productions/${prodId}/quote${q(companyId)}`);
  return data;
}

// ----- Multi-file quote (price proposal) -----

export async function addQuoteFile(prodId, { file, link, name }, companyId) {
  const fd = new FormData();
  if (file) fd.append("file", file);
  if (link) fd.append("link", link);
  if (name) fd.append("name", name);
  const { data } = await api.post(
    `/fullframe/productions/${prodId}/quote-files${q(companyId)}`,
    fd,
    { timeout: 120000 },
  );
  return data;
}

export async function renameQuoteFile(prodId, fileId, name, companyId) {
  const { data } = await api.patch(
    `/fullframe/productions/${prodId}/quote-files/${fileId}${q(companyId)}`,
    { name },
  );
  return data;
}

export async function deleteQuoteFile(prodId, fileId, companyId) {
  const { data } = await api.delete(
    `/fullframe/productions/${prodId}/quote-files/${fileId}${q(companyId)}`,
  );
  return data;
}

// ===================== PRODUCTION LIFECYCLE =====================

export async function markProductionComplete(prodId, companyId) {
  const { data } = await api.post(`/fullframe/productions/${prodId}/complete${q(companyId)}`);
  return data;
}

export async function reactivateProduction(prodId, companyId) {
  const { data } = await api.post(`/fullframe/productions/${prodId}/reactivate${q(companyId)}`);
  return data;
}

// ===================== BACKUPS =====================/** List all backups (most recent first). */
export async function listBackups(companyId) {
  const { data } = await api.get(`/fullframe/backups${q(companyId)}`);
  return data;
}

/** Trigger Recovery Playbook ZIP build for an existing backup. The
 *  endpoint assembles DB + assets + config into a single ZIP, uploads
 *  it to the per-backup Drive subfolder, and persists md5/size on the
 *  backup row so the UI can show it.  Returns the metadata. */
export async function buildRecoveryZip(backupId, companyId) {
  const { data } = await api.post(
    `/fullframe/backups/${backupId}/recovery-zip${q(companyId)}`,
  );
  return data;
}

/** Download the Recovery Playbook ZIP for a backup straight into the
 *  browser. */
export async function downloadRecoveryZip(backupId, companyId) {
  const url = `/fullframe/backups/${backupId}/recovery-zip/download${q(companyId)}`;
  const res = await api.get(url, { responseType: "blob" });
  const cd = res.headers["content-disposition"] || "";
  const m = cd.match(/filename="?([^"]+)"?/i);
  const filename = (m && m[1]) || `flowup-recovery-${backupId.slice(0, 8)}.zip`;
  const blobUrl = window.URL.createObjectURL(new Blob([res.data], { type: "application/zip" }));
  const a = document.createElement("a");
  a.href = blobUrl;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  window.URL.revokeObjectURL(blobUrl);
}

/** Upload a Recovery Playbook ZIP and restore the company from it. The
 *  endpoint verifies every checksum inside `manifest.json` before
 *  touching the database. */
export async function restoreFromRecoveryZip(file, companyId) {
  const form = new FormData();
  form.append("file", file);
  const { data } = await api.post(
    `/fullframe/backups/restore-from-zip${q(companyId)}`,
    form,
    { headers: { "Content-Type": "multipart/form-data" } },
  );
  return data;
}

/** Create a new backup snapshot. `label` is optional (a default
 *  timestamp-based label is used when omitted). */
export async function createBackup(label, companyId) {
  const { data } = await api.post(
    `/fullframe/backups${q(companyId)}`,
    { label: label || "" },
  );
  return data;
}

/** Restore the company's data from a backup. The current state is
 *  auto-archived first as a safety net. Returns `safety_backup_id`. */
export async function restoreBackup(backupId, companyId) {
  const { data } = await api.post(
    `/fullframe/backups/${backupId}/restore${q(companyId)}`,
  );
  return data;
}

export async function deleteBackup(backupId, companyId) {
  const { data } = await api.delete(
    `/fullframe/backups/${backupId}${q(companyId)}`,
  );
  return data;
}

/** Download the full snapshot as a `.json` file via the browser. Triggers
 *  a real file download (Content-Disposition: attachment) so the user can
 *  archive it on disk, reupload to Drive manually, or import elsewhere. */
export async function downloadBackup(backupId, companyId) {
  const url = `/fullframe/backups/${backupId}/export${q(companyId)}`;
  const res = await api.get(url, { responseType: "blob" });
  // Parse filename from Content-Disposition when available.
  const cd = res.headers["content-disposition"] || "";
  const m = cd.match(/filename="?([^"]+)"?/i);
  const filename = (m && m[1]) || `flowup-backup-${backupId.slice(0, 8)}.json`;
  const blobUrl = window.URL.createObjectURL(new Blob([res.data], { type: "application/json" }));
  const a = document.createElement("a");
  a.href = blobUrl;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  window.URL.revokeObjectURL(blobUrl);
}

/**
 * Resolve a company id from a URL slug. Used by both Dashboard and
 * Production pages to wire `companyId` into every subsequent api call —
 * critical for super_admin browsing tenant pages.
 */
export async function resolveCompanyIdBySlug(slug) {
  const { data } = await api.get(`/companies/by-slug/${encodeURIComponent(slug)}`);
  return data?.id || null;
}

/** Same lookup but returns the FULL company document — used by the admin
 *  sidebar to read keys / addons. Falls back to a minimal `{id}` object
 *  if the user isn't allowed to see secrets. */
export async function getCompanyBySlug(slug) {
  const { data } = await api.get(`/companies/by-slug/${encodeURIComponent(slug)}`);
  return data || null;
}


/** Owner spec 2026-07-08l — Public share link.
 *  mintShareLink(prodId, companyId, regenerate?) — auth required.
 *  getPublicProduction(token) — no auth; used by the /share/... route.
 */
export async function mintShareLink(prodId, companyId, regenerate = false) {
  const parts = [];
  if (companyId) parts.push(`company_id=${encodeURIComponent(companyId)}`);
  if (regenerate) parts.push(`regenerate=true`);
  const query = parts.length ? `?${parts.join("&")}` : "";
  const { data } = await api.post(`/fullframe/productions/${prodId}/share-link${query}`);
  return data;
}

export async function getPublicProduction(token) {
  const { data } = await api.get(`/fullframe/public/productions/${encodeURIComponent(token)}`);
  return data;
}

/** Owner spec 2026-07-08t — Crew document uploads. */
export async function uploadCrewDocument(prodId, { file, docType, notes = "" }, companyId) {
  const form = new FormData();
  form.append("file", file);
  form.append("doc_type", docType);
  form.append("notes", notes);
  const query = companyId ? `?company_id=${encodeURIComponent(companyId)}` : "";
  const { data } = await api.post(
    `/fullframe/productions/${prodId}/crew-documents${query}`,
    form,
    { headers: { "Content-Type": "multipart/form-data" } }
  );
  return data;
}

export async function deleteCrewDocument(prodId, docId, companyId) {
  const query = companyId ? `?company_id=${encodeURIComponent(companyId)}` : "";
  const { data } = await api.delete(`/fullframe/productions/${prodId}/crew-documents/${docId}${query}`);
  return data;
}


/** Owner spec 2026-02-07 — log a taxi order against a crew row. Body:
 *  { date, time, taxi_company, taxi_phone, pickup, dropoff, pax, notes,
 *    target: 'crew'|'taxi'|'both' }
 */
export async function addCrewTaxiOrder(prodId, rowId, payload, companyId) {
  const query = companyId ? `?company_id=${encodeURIComponent(companyId)}` : "";
  const { data } = await api.post(
    `/fullframe/productions/${prodId}/crew/${rowId}/taxi-orders${query}`,
    payload,
  );
  return data;
}

/** Owner spec 2026-02-07e — append a saved taxi card. Returns the new
 *  card entry with generated id + saved_at. */
export async function saveCrewTaxiDefaults(prodId, rowId, payload, companyId) {
  const query = companyId ? `?company_id=${encodeURIComponent(companyId)}` : "";
  const { data } = await api.put(
    `/fullframe/productions/${prodId}/crew/${rowId}/taxi-defaults${query}`,
    payload,
  );
  return data;
}

/** Delete a saved taxi card by id. */
export async function deleteTaxiSavedCard(prodId, cardId, companyId) {
  const query = companyId ? `?company_id=${encodeURIComponent(companyId)}` : "";
  const { data } = await api.delete(
    `/fullframe/productions/${prodId}/taxi-saved-cards/${cardId}${query}`,
  );
  return data;
}
